import type {
  DrillAttendance,
  DrillRating,
  DrillRecordIssue,
  DrillRecordStage,
  DrillStepResult,
} from '@felix/domain';
import type { DrillRecord, DrillRecordListItem, DrillRecordState } from './record-types.js';

/**
 * Execution Wizard'ın saf yardımcıları (ADR 0128 Faz 3 Ek): aşamalar, taslaklar, gönderilecek
 * gövde, saat ve süre biçimi. Ekrandan bağımsız test edilir.
 */

export const WIZARD_STAGES = [
  'general',
  'preparation',
  'participants',
  'execution',
  'evaluation',
  'close',
] as const;
export type WizardStage = (typeof WIZARD_STAGES)[number];

/** Ortağın belgesindeki harfler: A General … F Close. */
export const STAGE_LETTER: Readonly<Record<WizardStage, string>> = {
  general: 'A',
  preparation: 'B',
  participants: 'C',
  execution: 'D',
  evaluation: 'E',
  close: 'F',
};

/** Aşamanın gönderim eksikleri (F Close bütün eksikleri gösterir). */
export function stageIssues(
  issues: readonly DrillRecordIssue[],
  stage: WizardStage,
): DrillRecordIssue[] {
  if (stage === 'close') return [...issues];
  return issues.filter((issue) => issue.stage === (stage as DrillRecordStage));
}

/* ---------- Taslaklar ---------- */

export interface GeneralDraft {
  drillDate: string;
  startTime: string;
  endTime: string;
  location: string;
  responsibleUserId: string | null;
  scenario: string;
}

export interface PreparationDraft {
  raVerificationId: string | null;
  riskNotes: string;
  briefingDone: boolean | null;
  briefingNotes: string;
  equipmentReady: boolean | null;
  equipmentNotes: string;
  weather: string;
  constraints: string;
  checklist: { item: string; done: boolean }[];
}

export interface ParticipantDraft {
  /** Kayıtlı satır; yeni eklenen kişide yok. */
  id: string | null;
  rowVersion: number | null;
  userId: string | null;
  name: string;
  rank: string | null;
  role: string;
  attendance: DrillAttendance | null;
  absenceReason: string;
}

export interface StepDraft {
  id: string;
  rowVersion: number;
  result: DrillStepResult | null;
  remarks: string;
  responsibleUserId: string | null;
  doneTime: string;
}

export interface EvaluationDraft {
  results: Record<string, { rating: DrillRating | null; seconds: string; note: string }>;
  debriefing: string;
  /** Kişi değerlendirmesi (Crew Competency Matrix; Faz 4 Ek): kullanıcı → değerlendirme. */
  crew: Record<string, { rating: DrillRating | null; note: string }>;
}

export interface RecordDrafts {
  general: GeneralDraft;
  preparation: PreparationDraft;
  participants: ParticipantDraft[];
  steps: StepDraft[];
  evaluation: EvaluationDraft;
}

const str = (value: string | null | undefined) => value ?? '';
const orNull = (value: string) => (value.trim() === '' ? null : value.trim());

/** Kayıttan düzenlenebilir taslaklar. */
export function draftsOf(record: DrillRecord): RecordDrafts {
  const results: EvaluationDraft['results'] = {};
  for (const criterion of record.criteria) {
    const found = record.evaluation.results.find((r) => r.key === criterion.key);
    results[criterion.key] = {
      rating: found?.rating ?? null,
      seconds: found?.seconds == null ? '' : formatSeconds(found.seconds),
      note: str(found?.note),
    };
  }
  return {
    general: {
      drillDate: record.general.drillDate,
      startTime: str(record.general.startTime),
      endTime: str(record.general.endTime),
      location: str(record.general.location),
      responsibleUserId: record.general.responsibleUserId,
      scenario: str(record.general.scenario),
    },
    preparation: {
      raVerificationId: record.preparation.raVerificationId,
      riskNotes: str(record.preparation.riskNotes),
      briefingDone: record.preparation.briefingDone,
      briefingNotes: str(record.preparation.briefingNotes),
      equipmentReady: record.preparation.equipmentReady,
      equipmentNotes: str(record.preparation.equipmentNotes),
      weather: str(record.preparation.weather),
      constraints: str(record.preparation.constraints),
      checklist: record.preparation.checklist.map((c) => ({ ...c })),
    },
    participants: record.participants.map((p) => ({
      id: p.id,
      rowVersion: p.rowVersion,
      userId: p.userId,
      name: p.name,
      rank: p.rank,
      role: str(p.role),
      attendance: p.attendance,
      absenceReason: str(p.absenceReason),
    })),
    steps: record.steps.map((s) => ({
      id: s.id,
      rowVersion: s.rowVersion,
      result: s.result,
      remarks: str(s.remarks),
      responsibleUserId: s.responsibleUserId,
      doneTime: str(s.doneTime),
    })),
    evaluation: {
      results,
      debriefing: str(record.evaluation.debriefing),
      crew: Object.fromEntries(
        (record.evaluation.crew ?? []).map((c) => [
          c.userId,
          { rating: c.rating, note: str(c.note) },
        ]),
      ),
    },
  };
}

/** Aşamanın sunucuya gidecek gövdesi (`saveDrillRecord` aşama alanı). */
export function stagePayload(
  stage: Exclude<WizardStage, 'close'>,
  drafts: RecordDrafts,
): Record<string, unknown> {
  switch (stage) {
    case 'general': {
      const g = drafts.general;
      return {
        general: {
          drillDate: g.drillDate,
          startTime: orNull(g.startTime),
          endTime: orNull(g.endTime),
          location: orNull(g.location),
          responsibleUserId: g.responsibleUserId,
          scenario: orNull(g.scenario),
        },
      };
    }
    case 'preparation': {
      const p = drafts.preparation;
      return {
        preparation: {
          raVerificationId: p.raVerificationId,
          riskNotes: orNull(p.riskNotes),
          briefingDone: p.briefingDone,
          briefingNotes: orNull(p.briefingNotes),
          equipmentReady: p.equipmentReady,
          equipmentNotes: orNull(p.equipmentNotes),
          weather: orNull(p.weather),
          constraints: orNull(p.constraints),
          checklist: p.checklist,
        },
      };
    }
    case 'participants':
      return {
        participants: drafts.participants.map((p) => ({
          ...(p.id ? { id: p.id, rowVersion: p.rowVersion } : {}),
          userId: p.userId,
          name: p.name,
          rank: p.rank,
          role: orNull(p.role),
          attendance: p.attendance,
          absenceReason: p.attendance === 'absent' ? orNull(p.absenceReason) : null,
        })),
      };
    case 'execution':
      return {
        steps: drafts.steps.map((s) => ({
          id: s.id,
          rowVersion: s.rowVersion,
          result: s.result,
          remarks: orNull(s.remarks),
          responsibleUserId: s.responsibleUserId,
          doneTime: orNull(s.doneTime),
        })),
      };
    case 'evaluation':
      return {
        evaluation: {
          results: Object.entries(drafts.evaluation.results)
            .map(([key, value]) => ({
              key,
              rating: value.rating,
              seconds: parseSeconds(value.seconds),
              note: orNull(value.note),
            }))
            .filter((r) => r.rating !== null || r.seconds !== null || r.note !== null),
          debriefing: orNull(drafts.evaluation.debriefing),
          crew: Object.entries(drafts.evaluation.crew)
            .map(([userId, value]) => ({ userId, rating: value.rating, note: orNull(value.note) }))
            .filter((c) => c.rating !== null || c.note !== null),
        },
      };
  }
}

/* ---------- Saat ve süre ---------- */

/** "930", "9:30", "0930" → "09:30"; çözülemezse olduğu gibi (sunucu `INVALID_TIME` verir). */
export function normalizeTime(value: string): string {
  const text = value.trim();
  if (text === '') return '';
  const match = /^(\d{1,2})[:.]?(\d{2})$/.exec(text);
  if (!match) return text;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return text;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** Gemi saatiyle şimdi (`HH:MM`); gemi saati farkı kayıttan. */
export function shipTimeNow(now: Date, tzOffsetMin: number): string {
  return new Date(now.getTime() + tzOffsetMin * 60_000).toISOString().slice(11, 16);
}

/** Saniye → `m:ss` (saat üstünde `h:mm:ss`). */
export function formatSeconds(total: number): string {
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const ss = String(seconds).padStart(2, '0');
  return hours > 0 ? `${hours}:${String(minutes).padStart(2, '0')}:${ss}` : `${minutes}:${ss}`;
}

/** `m:ss`, `h:mm:ss` ya da yalnız dakika → saniye; boş ya da çözülemezse `null`. */
export function parseSeconds(value: string): number | null {
  const text = value.trim();
  if (text === '') return null;
  if (/^\d+$/.test(text)) return Number(text) * 60;
  const parts = text.split(':');
  if (parts.length < 2 || parts.length > 3 || parts.some((p) => !/^\d+$/.test(p))) return null;
  const numbers = parts.map(Number);
  const [a = 0, b = 0, c] = numbers;
  if (c === undefined) return b > 59 ? null : a * 60 + b;
  return b > 59 || c > 59 ? null : a * 3600 + b * 60 + c;
}

/* ---------- Liste ---------- */

export const RECORD_FILTERS = [
  'all',
  'in_progress',
  'submitted',
  'approved',
  'stopped',
  'cancelled',
] as const;
export type RecordFilter = (typeof RECORD_FILTERS)[number];

/** Liste süzgeci ve sırası: süren ve inceleme bekleyen önce, sonra yeniden eskiye. */
export function filterRecords(
  records: readonly DrillRecordListItem[],
  filter: RecordFilter,
): DrillRecordListItem[] {
  const order: Record<DrillRecordState, number> = {
    in_progress: 0,
    submitted: 1,
    stopped: 2,
    approved: 3,
    cancelled: 4,
  };
  return records
    .filter((r) => filter === 'all' || r.state === filter)
    .sort(
      (a, b) =>
        order[a.state] - order[b.state] ||
        b.drillDate.localeCompare(a.drillDate) ||
        b.recordNo.localeCompare(a.recordNo),
    );
}
